import { toJS } from 'mobx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CachedMasterDataService } from '../../../common/api/cached-master-data-service';
import type { TimeSliderConfig } from '../../../common/filter/filter-store';
import type { MetricStore } from '../../../common/metrics/metric-store';
import { getAliasForMetricGroupId } from '../../../common/v2/api/alias/utils';
import type { Filter } from '../../../common/v2/common/components/filter/filterbar/types';
import type {
  Months,
  TimeSliderState,
} from '../../../common/v2/common/components/timeslider/types';
import {
  useAliasesContext,
  useGlobalLocaleContext,
} from '../../../common/v2/context/contexts';
import type { DataFieldWithDataType } from '../../../common/v2/types';
import { getEmployeeIdsForPbsCell } from './services/getEmployeeIdsForPbsCell';
import { timeContextForEmployeeList } from './services/timeContextForEmployeeList';
import { getDisplayMetricGroupName } from './translations';
import type { PbsCellDrillDownContext, PbsTimeHeaderData } from './types';

interface UsePbsCellDrillDownParams {
  filters: Filter[];
  timeSliderConfig: TimeSliderConfig;
  metricStore: MetricStore;
  cachedMasterDataApiService: CachedMasterDataService | null | undefined;
  segmentLevel1Dimension: DataFieldWithDataType | null;
  segmentLevel2Dimension: DataFieldWithDataType | null;
  /** When false, cells are rendered without drill-down interaction. */
  enabled?: boolean;
}

/** All props for PbsCellDrillDownModal except timeHeaderObjs, which lives in a ref in BalanceSheetTable. */
export interface DrillDownModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  employeeIds: string[];
  metricValueByEmployeeId: Record<string, number>;
  error: string | null;
  timeSliderState: TimeSliderState | null;
  firstMonthOfYear: Months | null;
  endDate: string | null;
  drillDownContext: PbsCellDrillDownContext | null;
  measureLabel: string;
  timeHeadersData: PbsTimeHeaderData[];
  onLoadForContext: (context: PbsCellDrillDownContext) => void;
}

export function usePbsCellDrillDown({
  filters,
  timeSliderConfig,
  metricStore,
  cachedMasterDataApiService,
  segmentLevel1Dimension,
  segmentLevel2Dimension,
  enabled = true,
}: UsePbsCellDrillDownParams): {
  onCellDrillDown?: (
    context: PbsCellDrillDownContext,
    timeHeadersData?: PbsTimeHeaderData[]
  ) => void;
  drillDownModalProps: DrillDownModalProps;
} {
  const aliases = useAliasesContext();
  const selectedLanguage = useGlobalLocaleContext().selected;

  const filtersSnapshotRef = useRef<Filter[]>([]);
  const drillDownTimeHeadersDataRef = useRef<PbsTimeHeaderData[]>([]);

  const [cellDrillDown, setCellDrillDown] = useState<{
    open: boolean;
    loading: boolean;
    employeeIds: string[];
    metricValueByEmployeeId: Record<string, number>;
    error: string | null;
    context: PbsCellDrillDownContext | null;
    timeHeadersData: PbsTimeHeaderData[];
  }>({
    open: false,
    loading: false,
    employeeIds: [],
    metricValueByEmployeeId: {},
    error: null,
    context: null,
    timeHeadersData: [],
  });

  /** Snapshot of filters last passed to the table service; used for drill-down so both use identical filters. */
  useEffect(() => {
    filtersSnapshotRef.current = toJS(filters) as Filter[];
  }, [filters]);

  const employeeListTimeContext = useMemo(
    () => timeContextForEmployeeList(timeSliderConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      timeSliderConfig.startDate,
      timeSliderConfig.endDate,
      timeSliderConfig.granularity,
      timeSliderConfig.firstMonthOfYear,
    ]
  );

  const fetchEmployeeIdsForContext = useCallback(
    (
      context: PbsCellDrillDownContext,
      timeHeadersData: PbsTimeHeaderData[]
    ) => {
      const filtersForDrillDown =
        filtersSnapshotRef.current.length > 0
          ? filtersSnapshotRef.current
          : (toJS(filters) as Filter[]);
      if (!cachedMasterDataApiService) {
        setCellDrillDown((prev) => ({
          ...prev,
          loading: false,
          error: 'Data service not available',
        }));
        return Promise.resolve();
      }
      return getEmployeeIdsForPbsCell({
        context,
        filters: filtersForDrillDown,
        timeSliderConfig,
        metricStore,
        cachedMasterDataApiService,
        segmentLevel1Dimension,
        segmentLevel2Dimension,
      }).then((result) => {
        if ('error' in result) {
          setCellDrillDown((prev) => ({
            ...prev,
            loading: false,
            error: result.error,
          }));
        } else {
          drillDownTimeHeadersDataRef.current = timeHeadersData;
          setCellDrillDown((prev) => ({
            ...prev,
            loading: false,
            employeeIds: result.employeeIds,
            metricValueByEmployeeId: result.metricValueByEmployeeId,
            error: null,
            context,
            timeHeadersData,
          }));
        }
      });
    },
    [
      filters,
      timeSliderConfig,
      metricStore,
      cachedMasterDataApiService,
      segmentLevel1Dimension,
      segmentLevel2Dimension,
    ]
  );

  const handleCellDrillDown = useCallback(
    (
      context: PbsCellDrillDownContext,
      timeHeadersData: PbsTimeHeaderData[] = []
    ) => {
      drillDownTimeHeadersDataRef.current = timeHeadersData;
      setCellDrillDown((prev) => ({
        ...prev,
        open: true,
        loading: true,
        employeeIds: [],
        metricValueByEmployeeId: {},
        error: null,
        context,
        timeHeadersData,
      }));
      fetchEmployeeIdsForContext(context, timeHeadersData).catch(() => {
        setCellDrillDown((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to load employee IDs',
        }));
      });
    },
    [fetchEmployeeIdsForContext]
  );

  const handleLoadForContext = useCallback(
    (context: PbsCellDrillDownContext) => {
      const timeHeadersData = drillDownTimeHeadersDataRef.current;
      setCellDrillDown((prev) => ({
        ...prev,
        loading: true,
        error: null,
        context,
        employeeIds: [],
        metricValueByEmployeeId: {},
      }));
      fetchEmployeeIdsForContext(context, timeHeadersData).catch(() => {
        setCellDrillDown((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to load employee IDs',
        }));
      });
    },
    [fetchEmployeeIdsForContext]
  );

  const closeModal = useCallback(() => {
    setCellDrillDown((prev) => ({
      ...prev,
      open: false,
      employeeIds: [],
      metricValueByEmployeeId: {},
      error: null,
    }));
  }, []);

  const measureLabel = useMemo(() => {
    if (!cellDrillDown.context) return '';
    const groupAlias = getAliasForMetricGroupId(
      aliases,
      selectedLanguage
    )(cellDrillDown.context.metricGroupId);
    const groupName = getDisplayMetricGroupName(
      cellDrillDown.context.metricGroupId,
      groupAlias,
      metricStore
    );
    const measureName = metricStore.getTranslatedMetricIdName(
      cellDrillDown.context.selectedMeasure
    );
    return `${groupName} (${measureName})`;
  }, [aliases, selectedLanguage, cellDrillDown.context, metricStore]);

  const drillDownModalProps: DrillDownModalProps = {
    open: cellDrillDown.open,
    onClose: closeModal,
    loading: cellDrillDown.loading,
    employeeIds: cellDrillDown.employeeIds,
    metricValueByEmployeeId: cellDrillDown.metricValueByEmployeeId,
    error: cellDrillDown.error,
    timeSliderState: employeeListTimeContext?.timeSliderState ?? null,
    firstMonthOfYear: employeeListTimeContext?.firstMonthOfYear ?? null,
    endDate: timeSliderConfig.endDate || null,
    drillDownContext: cellDrillDown.context,
    measureLabel,
    timeHeadersData: cellDrillDown.timeHeadersData,
    onLoadForContext: handleLoadForContext,
  };

  const onCellDrillDown = enabled ? handleCellDrillDown : undefined;

  return { onCellDrillDown, drillDownModalProps };
}
